import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../ui/alert-dialog';
import {
  Users, Search, User, Building2, Plus, Pencil, Trash2, Loader2, Landmark,
  CheckCircle2, FileText, FileSpreadsheet, History,
} from 'lucide-react';
import { toast } from 'sonner';
import { NominaExcelDialog } from './NominaExcelDialog';
import { NominasHistorialDialog } from './NominasHistorialDialog';
import { EgresoAlegraNominaFields, describirEgresoAlegra, egresoCfgInicial } from './EgresoAlegraNominaFields';

const EMPTY = {
  trabajador_numero: '',
  beneficiario_idx: null,       // null → principal
  rut_beneficiario: '',
  nombre_beneficiario: '',
  banco_codigo: '',
  banco_descripcion: '',
  cuenta_numero: '',
  tipo_cuenta: 'cta cte',
  email: '',
  _lock_trabajador: false,
};

/** Iter 308.j/k — Cuentas de Colaboradores (subvista de Cuentas Bancarias).
 * Fuente única: colección `trabajadores`. CRUD + bootstrap CTN.TXT + nómina TEF.
 * Acceso backend: admin/gerencia/rrhh_encargado.
 */
export function TefColaboradoresTab() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, trabajadores_count: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [soloPendientes, setSoloPendientes] = useState(false);

  const [bancos, setBancos] = useState([]);
  const [trabajadores, setTrabajadores] = useState([]);

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const [generandoCTN, setGenerandoCTN] = useState(false);
  const [genResult, setGenResult] = useState(null);

  const [nominaOpen, setNominaOpen] = useState(false);
  const [egresoCfg, setEgresoCfg] = useState(egresoCfgInicial());
  const [nominaMontos, setNominaMontos] = useState({});
  const [nominaMotivo, setNominaMotivo] = useState('');
  const [generandoNomina, setGenerandoNomina] = useState(false);
  const [excelOpen, setExcelOpen] = useState(false); // Iter 382 — nómina de sueldos desde Excel
  const [historialOpen, setHistorialOpen] = useState(false); // Iter 383
  const [historialKey, setHistorialKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/tef/cuentas-colaboradores', { params: { q, solo_pendientes: soloPendientes } });
      setItems(r.data?.items || []);
      setMeta({ total: r.data?.total || 0, trabajadores_count: r.data?.trabajadores_count || 0 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando cuentas de colaboradores');
    } finally {
      setLoading(false);
    }
  }, [q, soloPendientes]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/trabajadores/bancos').then((r) => setBancos(r.data?.bancos || [])).catch(() => {});
    api.get('/trabajadores', { params: { activo: true } })
      .then((r) => setTrabajadores(r.data || []))
      .catch(() => {});
  }, []);

  const abrirNuevo = () => setForm({ ...EMPTY });
  const abrirEditar = (row) => setForm({
    trabajador_numero: row.trabajador_numero,
    beneficiario_idx: row.tipo_cuenta_slot === 'principal' ? null : row.beneficiario_idx,
    rut_beneficiario: row.rut_beneficiario || '',
    nombre_beneficiario: row.nombre_beneficiario || '',
    banco_codigo: row.banco_codigo || '',
    banco_descripcion: row.banco_descripcion || '',
    cuenta_numero: row.cuenta_numero || '',
    tipo_cuenta: row.tipo_cuenta || 'cta cte',
    email: row.email || '',
    _lock_trabajador: true,
    _orig_slot: row.tipo_cuenta_slot,
    _orig_idx: row.tipo_cuenta_slot === 'principal' ? null : row.beneficiario_idx,
  });

  const guardar = async () => {
    if (!form.trabajador_numero) { toast.error('Elegí un colaborador'); return; }
    if (!form.banco_codigo) { toast.error('Elegí un banco'); return; }
    if (!(form.cuenta_numero || '').trim()) { toast.error('Ingresá el N° de cuenta'); return; }
    if (form.beneficiario_idx !== null && !(form.rut_beneficiario || '').trim()) {
      toast.error('RUT del beneficiario obligatorio para pagos a otro RUT'); return;
    }
    setSaving(true);
    try {
      const slotTarget = form.beneficiario_idx === null ? 'principal' : 'beneficiario';
      const cambioTipo = form._orig_slot && form._orig_slot !== slotTarget;
      await api.post('/tef/cuentas-colaboradores/upsert', {
        trabajador_numero: Number(form.trabajador_numero),
        beneficiario_idx: form.beneficiario_idx === null ? null : Number(form.beneficiario_idx),
        rut_beneficiario: form.rut_beneficiario || '',
        nombre_beneficiario: form.nombre_beneficiario || '',
        banco_codigo: String(form.banco_codigo),
        banco_descripcion: form.banco_descripcion || '',
        cuenta_numero: form.cuenta_numero.trim(),
        tipo_cuenta: form.tipo_cuenta || 'cta cte',
        email: form.email || '',
        convertir_desde: cambioTipo ? form._orig_slot : null,
        convertir_desde_idx: cambioTipo && form._orig_slot === 'beneficiario' ? form._orig_idx : null,
      });
      toast.success('Cuenta guardada');
      setForm(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const eliminar = async () => {
    if (!delTarget) return;
    try {
      const t = delTarget;
      const url = t.tipo_cuenta_slot === 'principal'
        ? `/tef/cuentas-colaboradores/${t.trabajador_numero}/cuenta`
        : `/tef/cuentas-colaboradores/${t.trabajador_numero}/beneficiario/${t.beneficiario_idx}`;
      await api.delete(url);
      toast.success('Cuenta eliminada');
      setDelTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al eliminar');
    }
  };

  const generarCTN = async () => {
    setGenerandoCTN(true); setGenResult(null);
    try {
      const r = await api.post('/tef/cuentas-colaboradores/generar-ctn');
      const data = r.data || {};
      setGenResult(data);
      if (!data.archivo) { toast.info(data.mensaje || 'Nada por cargar al banco.'); return; }
      const bin = atob(data.archivo.contenido_b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: 'text/plain' }));
      const a = document.createElement('a');
      a.href = url; a.download = data.archivo.filename || 'CTN.TXT';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
      toast.success(`CTN.TXT descargado — ${data.num_lineas} cuenta(s) marcada(s) como cargada(s).`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error generando CTN.TXT');
    } finally { setGenerandoCTN(false); }
  };

  const abrirNomina = () => {
    setNominaMontos({});
    setNominaMotivo('');
    setEgresoCfg((c) => ({ ...egresoCfgInicial(), cuenta_id: c.cuenta_id }));
    setNominaOpen(true);
  };

  const generarNomina = async () => {
    const lineas = Object.entries(nominaMontos)
      .filter(([, m]) => Number(m) > 0)
      .map(([fila_id, m]) => ({ fila_id, monto: Number(m) }));
    if (lineas.length === 0) { toast.error('Ingresá al menos un monto > 0'); return; }
    setGenerandoNomina(true);
    try {
      const r = await api.post('/tef/cuentas-colaboradores/generar-nomina', {
        lineas, motivo: nominaMotivo || '', egreso_alegra: egresoCfg,
      });
      const data = r.data || {};
      if (!data.archivo) { toast.info(data.mensaje || 'Nada por generar.'); return; }
      const bin = atob(data.archivo.contenido_b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: 'text/plain' }));
      const a = document.createElement('a');
      a.href = url; a.download = data.archivo.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
      toast.success(`Nómina generada · ${data.num_lineas} transacción(es).`);
      if (data.egreso_alegra) {
        const msg = describirEgresoAlegra(data.egreso_alegra);
        if (data.egreso_alegra.estado === 'creado') toast.success(msg, { duration: 8000 }); else toast.error(msg, { duration: 10000 });
      }
      setHistorialKey((k) => k + 1);
      setNominaOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error generando nómina');
    } finally { setGenerandoNomina(false); }
  };

  if (loading) return <Skeleton className="h-64 w-full" />;

  return (
    <Card data-testid="tef-cuentas-colaboradores">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-700" />
            <div>
              <h3 className="font-semibold">Cuentas de sueldo de colaboradores</h3>
              <p className="text-xs text-muted-foreground">
                {meta.total} cuenta(s) de {meta.trabajadores_count} colaborador(es) activo(s).
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={abrirNuevo} data-testid="btn-nueva-cuenta-colab" className="gap-1">
              <Plus className="w-3.5 h-3.5" /> Nueva
            </Button>
            <Button
              size="sm" variant="outline" onClick={generarCTN} disabled={generandoCTN}
              data-testid="btn-generar-ctn"
              className="gap-1 border-blue-300 text-blue-700 hover:bg-blue-50"
              title="Genera CTN.TXT (Carga de Trabajadores Nuevos) con las cuentas aún no cargadas al banco, monto $1"
            >
              {generandoCTN ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Landmark className="w-3.5 h-3.5" />}
              CTN.TXT
            </Button>
            <Button
              size="sm" onClick={abrirNomina}
              data-testid="btn-generar-nomina"
              className="gap-1 bg-emerald-600 hover:bg-emerald-700"
            >
              <FileText className="w-3.5 h-3.5" /> Genera Nómina
            </Button>
            <Button
              size="sm" variant="outline" onClick={() => setExcelOpen(true)}
              data-testid="btn-nomina-excel"
              className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              title="Sube un Excel con RUT · Nombre · Monto · Concepto y genera la nómina TEF de sueldos"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" /> Nómina desde Excel
            </Button>
            <Button
              size="sm" variant="ghost" onClick={() => setHistorialOpen(true)}
              data-testid="btn-historial-nominas" className="gap-1 text-slate-700"
              title="Nóminas TEF generadas (fecha, archivo, total, quién) con re-descarga"
            >
              <History className="w-3.5 h-3.5" /> Historial
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative w-full sm:w-72">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre, RUT, banco…"
              className="pl-8 h-9 text-sm"
              data-testid="input-colab-search"
            />
          </div>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="toggle-solo-pendientes">
            <input type="checkbox" checked={soloPendientes} onChange={(e) => setSoloPendientes(e.target.checked)} />
            Sólo pendientes de carga al banco
          </label>
        </div>

        {genResult && genResult.omitidas?.length > 0 && (
          <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
            <b>{genResult.omitidas.length}</b> cuenta(s) quedaron fuera del CTN.TXT · revisá &quot;Datos Banco (CMF)&quot;.
          </div>
        )}

        <div className="overflow-x-auto border rounded-md">
          <table className="w-full text-xs">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Colaborador</th>
                <th className="text-center px-3 py-2 font-semibold">Tipo</th>
                <th className="text-left px-3 py-2 font-semibold">RUT beneficiario</th>
                <th className="text-left px-3 py-2 font-semibold">Banco</th>
                <th className="text-left px-3 py-2 font-semibold">Cuenta</th>
                <th className="text-center px-3 py-2 font-semibold">Estado banco</th>
                <th className="text-right px-3 py-2 font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">
                  {q || soloPendientes ? 'Sin resultados con los filtros aplicados.' : 'No hay cuentas registradas.'}
                </td></tr>
              )}
              {items.map((it, idx) => (
                <tr key={it.id} className="border-t hover:bg-muted/30" data-testid={`colab-row-${idx}`}>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <User className="w-3 h-3 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{it.trabajador_nombre}</div>
                        <div className="text-[10px] text-muted-foreground">{it.cargo}{it.area ? ` · ${it.area}` : ''}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    {it.tipo_cuenta_slot === 'principal'
                      ? <Badge variant="secondary" className="text-[10px]">Principal</Badge>
                      : <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-300">Beneficiario</Badge>}
                  </td>
                  <td className="px-3 py-1.5 font-mono">{it.rut_beneficiario}</td>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-1">
                      <Building2 className="w-3 h-3 text-muted-foreground" />
                      <span>{it.banco_codigo}</span>
                      <span className="text-muted-foreground truncate max-w-[200px]">· {it.banco_descripcion}</span>
                    </div>
                  </td>
                  <td className="px-3 py-1.5 font-mono">{it.cuenta_numero}</td>
                  <td className="px-3 py-1.5 text-center">
                    {it.cargada_al_banco
                      ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" />Cargada</span>
                      : <span className="text-amber-700">Pendiente</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <Button size="icon" variant="ghost" onClick={() => abrirEditar(it)} className="h-7 w-7" data-testid={`btn-edit-colab-${idx}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => setDelTarget(it)} className="h-7 w-7 text-red-600" data-testid={`btn-del-colab-${idx}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>

      {/* Dialog Nueva/Editar */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-2xl" data-testid="dialog-cuenta-colab">
          <DialogHeader>
            <DialogTitle>{form?._lock_trabajador ? 'Editar cuenta' : 'Nueva cuenta de colaborador'}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2 space-y-1">
                <Label className="text-xs">Colaborador</Label>
                <Select
                  disabled={form._lock_trabajador}
                  value={form.trabajador_numero ? String(form.trabajador_numero) : ''}
                  onValueChange={(v) => setForm({ ...form, trabajador_numero: Number(v) })}
                >
                  <SelectTrigger data-testid="select-colab-trabajador"><SelectValue placeholder="Elegí un colaborador…" /></SelectTrigger>
                  <SelectContent>
                    {trabajadores.map((t) => (
                      <SelectItem key={t.numero} value={String(t.numero)}>
                        {t.nombre} {t.rut ? `· ${t.rut}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 space-y-1">
                <Label className="text-xs">Tipo</Label>
                <Select
                  value={form.beneficiario_idx === null ? 'principal' : 'beneficiario'}
                  onValueChange={(v) => setForm({
                    ...form,
                    beneficiario_idx: v === 'principal'
                      ? null
                      : (form._orig_slot === 'beneficiario' ? form._orig_idx : -1),
                  })}
                >
                  <SelectTrigger data-testid="select-tipo-cuenta-colab"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="principal">Cuenta principal (mismo RUT del colaborador)</SelectItem>
                    <SelectItem value="beneficiario">Beneficiario alterno (pagar a otro RUT)</SelectItem>
                  </SelectContent>
                </Select>
                {form._orig_slot && form._orig_slot !== (form.beneficiario_idx === null ? 'principal' : 'beneficiario') && (
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="hint-cambio-tipo-cuenta">
                    {form.beneficiario_idx === null
                      ? 'Al guardar, la cuenta pasará a usar el RUT del colaborador y se eliminará el beneficiario alterno.'
                      : 'Al guardar, el archivo bancario usará el RUT del beneficiario ingresado (la cuenta principal se limpia).'}
                  </p>
                )}
              </div>
              {form.beneficiario_idx !== null && (
                <>
                  <div className="space-y-1">
                    <Label className="text-xs">RUT Beneficiario</Label>
                    <Input value={form.rut_beneficiario} placeholder="12345678-9"
                      onChange={(e) => setForm({ ...form, rut_beneficiario: e.target.value })}
                      data-testid="input-benef-rut" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Nombre Beneficiario</Label>
                    <Input value={form.nombre_beneficiario}
                      onChange={(e) => setForm({ ...form, nombre_beneficiario: e.target.value })} />
                  </div>
                </>
              )}
              <div className="space-y-1">
                <Label className="text-xs">Banco</Label>
                <Select
                  value={form.banco_codigo ? String(form.banco_codigo) : ''}
                  onValueChange={(v) => {
                    const b = bancos.find((x) => String(x.codigo_cmf) === v);
                    setForm({ ...form, banco_codigo: v, banco_descripcion: b?.descripcion || '' });
                  }}
                >
                  <SelectTrigger data-testid="select-banco-colab"><SelectValue placeholder="Elegí banco…" /></SelectTrigger>
                  <SelectContent>
                    {bancos.map((b) => (
                      <SelectItem key={b.codigo_cmf} value={String(b.codigo_cmf)}>
                        {b.codigo_cmf} · {b.descripcion}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">N° Cuenta</Label>
                <Input value={form.cuenta_numero}
                  onChange={(e) => setForm({ ...form, cuenta_numero: e.target.value })}
                  data-testid="input-cuenta-colab" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Tipo de cuenta</Label>
                <Select value={form.tipo_cuenta}
                  onValueChange={(v) => setForm({ ...form, tipo_cuenta: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cta cte">Cuenta corriente</SelectItem>
                    <SelectItem value="cta vista">Cuenta vista</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Email (notificación)</Label>
                <Input type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
            <Button onClick={guardar} disabled={saving} data-testid="btn-save-cuenta-colab">
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog eliminar */}
      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar cuenta</AlertDialogTitle>
            <AlertDialogDescription>
              {delTarget && (
                <>Vas a eliminar la cuenta {delTarget.tipo_cuenta_slot === 'principal' ? 'principal' : `beneficiario ${delTarget.rut_beneficiario}`} de <b>{delTarget.trabajador_nombre}</b>. ¿Continuar?</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminar} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <NominaExcelDialog open={excelOpen} onClose={() => setExcelOpen(false)} onGenerated={() => { setHistorialKey((k) => k + 1); load(); }} />
      <NominasHistorialDialog open={historialOpen} onClose={() => setHistorialOpen(false)} refreshKey={historialKey} />

      {/* Dialog Nómina */}
      <Dialog open={nominaOpen} onOpenChange={setNominaOpen}>
        <DialogContent className="max-w-3xl" data-testid="dialog-nomina-colab">
          <DialogHeader>
            <DialogTitle>Genera Nómina TEF · Colaboradores</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-xs">Motivo (aparece en el TEF · máx 30 char)</Label>
              <Input value={nominaMotivo} onChange={(e) => setNominaMotivo(e.target.value.slice(0, 30))}
                placeholder="Ej: Sueldo Agosto 2026" />
            </div>
            <div className="max-h-96 overflow-y-auto border rounded">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-1.5">Colaborador</th>
                    <th className="text-center px-3 py-1.5">Tipo</th>
                    <th className="text-left px-3 py-1.5">Banco / Cuenta</th>
                    <th className="text-right px-3 py-1.5 w-40">Monto CLP</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={it.id} className="border-t">
                      <td className="px-3 py-1">{it.trabajador_nombre}</td>
                      <td className="px-3 py-1 text-center">
                        {it.tipo_cuenta_slot === 'principal' ? 'Principal' : `→ ${it.rut_beneficiario}`}
                      </td>
                      <td className="px-3 py-1 text-muted-foreground">
                        {it.banco_descripcion} · {it.cuenta_numero}
                      </td>
                      <td className="px-3 py-1 text-right">
                        <Input
                          type="number" min="0"
                          value={nominaMontos[it.id] || ''}
                          onChange={(e) => setNominaMontos({ ...nominaMontos, [it.id]: e.target.value })}
                          className="text-right h-7 text-xs"
                          data-testid={`nomina-monto-${i}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-xs text-muted-foreground">
              Total: ${Object.values(nominaMontos).reduce((s, m) => s + (Number(m) || 0), 0).toLocaleString('es-CL')}
            </div>
            <EgresoAlegraNominaFields open={nominaOpen} value={egresoCfg} onChange={setEgresoCfg}
              total={Object.values(nominaMontos).reduce((s, m) => s + (Number(m) || 0), 0)} testidPrefix="nomina-egreso-alegra" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNominaOpen(false)}>Cancelar</Button>
            <Button onClick={generarNomina} disabled={generandoNomina}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-descargar-nomina">
              {generandoNomina && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
              Generar y descargar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
